import { FC, ReactNode } from 'react';
import { MenuIcon } from './menu/MenuIcon';
import { Circle } from 'lucide-react';
import { useCurrentPath } from './menu/useCurrentPath';
import { useSidebar } from './menu/SidebarContext';

type Props = {
  to: string;
  title: string;
  icon?: string;
  fontIcon?: string;
  hasBullet?: boolean;
  isActive?: boolean;
  depth?: number;
  children?: ReactNode;
};

const AsideMenuItem: FC<Props> = ({
  children,
  to,
  title,
  icon,
  hasBullet = false,
  depth = 0,
}) => {
  const currentPath = useCurrentPath();
  const { setCollapsed } = useSidebar();

  const isActive = to ? currentPath === to : false;
  const paddingLeft = depth > 0 ? 14 + depth * 14 : 14;

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    if (to) {
      window.history.pushState({}, '', to);
      window.dispatchEvent(new PopStateEvent('popstate'));
    }

    if (window.innerWidth < 1024) {
      setCollapsed(true);
    }
  };

  return (
    <div>
      <a
        href={to || '#'}
        onClick={handleClick}
        className={`
          group flex items-center gap-3 px-3 py-3 mx-2 rounded-lg text-[13.5px] font-medium
          transition-all duration-200 ease-out
          ${isActive
            ? 'bg-white/15 text-white shadow-sm shadow-black/10'
            : 'text-slate-200 hover:bg-white/10 hover:text-white'
          }
        `}
        style={{ paddingLeft: `${paddingLeft}px` }}
      >
        {hasBullet && (
          <Circle
            size={6}
            className={`flex-shrink-0 fill-current ${
              isActive ? 'text-white' : 'text-slate-500 group-hover:text-slate-300'
            }`}
          />
        )}

        {icon && (
          <span
            className={`
              flex items-center justify-center w-9 h-9 rounded-lg flex-shrink-0
              transition-all duration-200
              ${isActive
                ? 'bg-white/20 text-white'
                : 'bg-white/[0.07] text-slate-300 group-hover:bg-white/[0.12] group-hover:text-white'
              }
            `}
          >
            <MenuIcon icon={icon} size={20} />
          </span>
        )}

        <span className="truncate leading-snug">{title}</span>
      </a>

      {children}
    </div>
  );
};

export { AsideMenuItem };